import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AskAnExpert, RequirementIntake, ViewService } from 'api';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { submit } from '@angular/forms/signals';
import { firstValueFrom } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DialogComponent } from 'common/dialog/dialog.component';
import { DialogHeaderTitleDirective } from 'common/dialog/dialog-slots.directive';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-ask-an-expert',
  imports: [
    MatDialogClose,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatProgressSpinner,
    DialogComponent,
    DialogHeaderTitleDirective,
    CardButtonsComponent,
    AutofocusDirective,
    ButtonDirective,
  ],
  templateUrl: 'ask-an-expert.component.html',
})
export class AskAnExpertComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly viewService = inject(ViewService);
  private readonly dialogRef = inject(MatDialogRef);
  readonly data = inject<{ requirement: RequirementIntake | null }>(MAT_DIALOG_DATA);

  form!: FormGroup;

  loading = signal(false);
  submitting = signal(false);
  submitted = signal(false);

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      question: ['', [Validators.required, this.noWhitespaceValidator]],
    });
  }

  public noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length ? null : { whitespace: true };
  }

  async onSubmit() {
    this.submitted.set(true);

    // stop here if form is invalid
    if (this.form.invalid) {
      // TODO: Display validation errors
      return;
    }

    this.submitting.set(true);
    try {
      await firstValueFrom(this.sendQuestion());
      this.dialogRef.close();
    } catch (error) {
      console.log(error);
      this.submitting.set(false);
    }
  }

  private sendQuestion() {
    const askAnExpert: AskAnExpert = {
      question: this.form.value.question,
      url: window.location.href,
      requirementApplicationId: this.getRequirementApplicationId(),
    };
    return this.viewService.askAnExpert(askAnExpert);
  }

  private getRequirementApplicationId() {
    // It's a bit confusing because the query param requirementId is actually the requirementApplicationId in the backend.
    const requirementIdParam = this.route.snapshot.queryParamMap.get('requirementId');
    const requirementId = requirementIdParam ? Number.parseInt(requirementIdParam, 10) : undefined;
    return Number.isNaN(requirementId) ? undefined : requirementId;
  }

  protected readonly submit = submit;
}
